/** @format */

import { styled, keyframes } from "@mui/material/styles";
import { Button } from "@mui/material";
import { colors } from "../../utility/types/constant";

/**
 * Set at runtime by CircleHero so the hero can slide up behind the floating
 * navbar pill. Defaults to 0px so a failed measurement never breaks layout.
 */
export const NAV_OFFSET = "var(--ff-nav-offset, 0px)";

// ── Keyframes ────────────────────────────────────────────────────────────────

export const gradientPan = keyframes`
	0% { background-position: 0% 50%; }
	50% { background-position: 100% 50%; }
	100% { background-position: 0% 50%; }
`;

export const orbDrift = keyframes`
	0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
	50% { transform: translate3d(0, -26px, 0) scale(1.08); }
`;

export const floatY = keyframes`
	0%, 100% { transform: translateY(0); }
	50% { transform: translateY(-12px); }
`;

export const pulseRing = keyframes`
	0% { transform: scale(0.9); opacity: 0.7; }
	70% { transform: scale(1.55); opacity: 0; }
	100% { transform: scale(1.55); opacity: 0; }
`;

export const livePulse = keyframes`
	0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(30,193,149,0.55); }
	50% { opacity: 0.55; box-shadow: 0 0 0 6px rgba(30,193,149,0); }
`;

export const shine = keyframes`
	0% { transform: translateX(-130%) skewX(-18deg); }
	55%, 100% { transform: translateX(240%) skewX(-18deg); }
`;

export const riseIn = keyframes`
	from { opacity: 0; transform: translateY(10px) scale(0.92); }
	to { opacity: 1; transform: translateY(0) scale(1); }
`;

export const heroEnter = keyframes`
	from { opacity: 0; transform: translateY(22px); }
	to { opacity: 1; transform: translateY(0); }
`;

// ── Hero shell ───────────────────────────────────────────────────────────────

export const HeroSection = styled("section")(({ theme }) => ({
    position: "relative",
    isolation: "isolate",
    overflow: "hidden",
    marginTop: `calc(-1 * ${NAV_OFFSET})`,
    paddingTop: `calc(${NAV_OFFSET} + clamp(28px, 4vw, 56px))`,
    paddingBottom: "clamp(72px, 9vw, 116px)",
    paddingLeft: "20px",
    paddingRight: "20px",
    background:
        "linear-gradient(125deg, #030a33 0%, #040d45 42%, #0a2159 72%, #06174a 100%)",
    backgroundSize: "220% 220%",
    animation: `${gradientPan} 22s ease infinite`,
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    [theme.breakpoints.down("sm")]: {
        paddingLeft: "16px",
        paddingRight: "16px",
    },
}));

export const GridOverlay = styled("div")({
    position: "absolute",
    inset: 0,
    zIndex: -1,
    backgroundImage:
        "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
    backgroundSize: "48px 48px",
    maskImage:
        "radial-gradient(ellipse 75% 65% at 45% 35%, #000 0%, transparent 82%)",
    WebkitMaskImage:
        "radial-gradient(ellipse 75% 65% at 45% 35%, #000 0%, transparent 82%)",
    pointerEvents: "none",
});

export const Orb = styled("div")({
    position: "absolute",
    zIndex: -1,
    borderRadius: "50%",
    filter: "blur(70px)",
    pointerEvents: "none",
    animation: `${orbDrift} 16s ease-in-out infinite`,
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
});

// ── Hero copy ────────────────────────────────────────────────────────────────

export const HeroBadge = styled("span")({
    display: "inline-flex",
    alignItems: "center",
    gap: "9px",
    background: "rgba(30,193,149,0.12)",
    border: "1px solid rgba(30,193,149,0.32)",
    color: "#7defc4",
    borderRadius: "100px",
    padding: "7px 16px",
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    animation: `${heroEnter} 0.7s cubic-bezier(0.22,0.61,0.36,1) both`,
});

export const LiveDot = styled("span")({
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: colors.mainSecondary,
    flexShrink: 0,
    animation: `${livePulse} 2.1s ease-in-out infinite`,
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
});

export const HeroTitle = styled("h1")(({ theme }) => ({
    margin: "20px 0 0",
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "clamp(2.15rem, 4.6vw, 3.9rem)",
    fontWeight: 800,
    lineHeight: 1.08,
    letterSpacing: "-0.03em",
    color: "#ffffff",
    animation: `${heroEnter} 0.7s cubic-bezier(0.22,0.61,0.36,1) 0.06s both`,
    [theme.breakpoints.down("md")]: { textAlign: "center" },
}));

export const TealSpan = styled("span")({
    background: "linear-gradient(120deg, #34e0ad 0%, #1ec195 55%, #7defc4 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
});

export const HeroSubtitle = styled("p")(({ theme }) => ({
    margin: "20px 0 0",
    maxWidth: "560px",
    fontFamily: "'Inter', 'Roboto Condensed', sans-serif",
    fontSize: "clamp(1rem, 1.6vw, 1.16rem)",
    lineHeight: 1.72,
    color: "rgba(255,255,255,0.7)",
    animation: `${heroEnter} 0.7s cubic-bezier(0.22,0.61,0.36,1) 0.12s both`,
    [theme.breakpoints.down("md")]: { textAlign: "center", marginInline: "auto" },
}));

export const CtaRow = styled("div")(({ theme }) => ({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "14px",
    marginTop: "32px",
    animation: `${heroEnter} 0.7s cubic-bezier(0.22,0.61,0.36,1) 0.18s both`,
    [theme.breakpoints.down("md")]: { justifyContent: "center" },
    [theme.breakpoints.down("sm")]: { flexDirection: "column", gap: "12px" },
}));

const baseCta = {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    fontSize: "1rem",
    textTransform: "none" as const,
    borderRadius: "14px",
    padding: "14px 30px",
    whiteSpace: "nowrap" as const,
    transition: "all 0.22s ease",
};

export const GoldButton = styled(Button)(({ theme }) => ({
    ...baseCta,
    position: "relative",
    overflow: "hidden",
    background: "linear-gradient(135deg, #ffd02e 0%, #ffaa00 100%)",
    color: colors.primary,
    boxShadow: "0 12px 34px rgba(255,176,0,0.38)",
    "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        width: "38%",
        background:
            "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)",
        animation: `${shine} 3.6s ease-in-out infinite`,
        pointerEvents: "none",
    },
    "&:hover": {
        background: "linear-gradient(135deg, #ffe05a 0%, #ffbb1a 100%)",
        boxShadow: "0 18px 44px rgba(255,176,0,0.5)",
        transform: "translateY(-2px)",
    },
    "@media (prefers-reduced-motion: reduce)": { "&::after": { animation: "none" } },
    [theme.breakpoints.down("sm")]: { width: "100%" },
}));

export const GhostButton = styled(Button)(({ theme }) => ({
    ...baseCta,
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.22)",
    color: "#ffffff",
    backdropFilter: "blur(6px)",
    "&:hover": {
        background: "rgba(255,255,255,0.12)",
        borderColor: "rgba(52,224,173,0.6)",
        transform: "translateY(-2px)",
    },
    [theme.breakpoints.down("sm")]: { width: "100%" },
}));

export const LinkButton = styled(Button)({
    ...baseCta,
    padding: "14px 10px",
    fontSize: "0.94rem",
    color: "rgba(255,255,255,0.72)",
    "&:hover": { background: "transparent", color: colors.mainSecondary },
});

export const TrustRow = styled("ul")(({ theme }) => ({
    listStyle: "none",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "10px 22px",
    margin: "30px 0 0",
    padding: 0,
    animation: `${heroEnter} 0.7s cubic-bezier(0.22,0.61,0.36,1) 0.26s both`,
    [theme.breakpoints.down("md")]: { justifyContent: "center" },
}));

export const TrustItem = styled("li")({
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.82rem",
    fontWeight: 500,
    color: "rgba(255,255,255,0.62)",
});

export const FinePrint = styled("p")(({ theme }) => ({
    margin: "22px 0 0",
    maxWidth: "540px",
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.76rem",
    lineHeight: 1.65,
    color: "rgba(255,255,255,0.46)",
    [theme.breakpoints.down("md")]: { textAlign: "center", marginInline: "auto" },
}));

// ── Hero product visual ──────────────────────────────────────────────────────

export const VisualWrap = styled("div")(({ theme }) => ({
    position: "relative",
    display: "flex",
    justifyContent: "center",
    paddingBlock: "34px",
    animation: `${heroEnter} 0.8s cubic-bezier(0.22,0.61,0.36,1) 0.2s both`,
    [theme.breakpoints.down("md")]: { marginTop: "48px" },
}));

export const GlassCard = styled("div")(({ theme }) => ({
    position: "relative",
    zIndex: 2,
    width: "100%",
    maxWidth: "430px",
    borderRadius: "24px",
    padding: "22px 22px 18px",
    background:
        "linear-gradient(165deg, rgba(255,255,255,0.11) 0%, rgba(255,255,255,0.045) 100%)",
    border: "1px solid rgba(255,255,255,0.16)",
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    boxShadow: "0 40px 90px rgba(1,6,32,0.6)",
    [theme.breakpoints.down("sm")]: { padding: "18px 16px 16px" },
}));

export const CardHeader = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "6px",
});

export const CardLabel = styled("span")({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.6)",
});

export const RingStage = styled("div")({
    position: "relative",
    width: "min(100%, 290px)",
    aspectRatio: "1 / 1",
    margin: "6px auto 4px",
});

export const RingTrack = styled("div")({
    position: "absolute",
    inset: "14%",
    borderRadius: "50%",
    border: "1px dashed rgba(255,255,255,0.2)",
});

export const RingProgress = styled("div")({
    position: "absolute",
    inset: "14%",
    borderRadius: "50%",
    transition: "background 0.45s ease",
    maskImage: "radial-gradient(circle, transparent 93%, #000 94%)",
    WebkitMaskImage: "radial-gradient(circle, transparent 93%, #000 94%)",
});

export const CenterPot = styled("div")({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "45%",
    aspectRatio: "1 / 1",
    borderRadius: "50%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    background:
        "radial-gradient(circle at 50% 30%, rgba(30,193,149,0.22) 0%, rgba(4,13,69,0.85) 70%)",
    border: "1px solid rgba(255,255,255,0.14)",
    boxShadow: "inset 0 0 28px rgba(30,193,149,0.16)",
});

export const PotAmount = styled("span", {
    shouldForwardProp: (prop) => prop !== "paying",
})<{ paying?: boolean }>(({ paying }) => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "clamp(1.05rem, 2.6vw, 1.42rem)",
    fontWeight: 800,
    letterSpacing: "-0.02em",
    lineHeight: 1.1,
    color: paying ? colors.secondaryMain : "#ffffff",
    transition: "color 0.3s ease, transform 0.3s ease",
    transform: paying ? "scale(1.08)" : "scale(1)",
}));

export const PotLabel = styled("span")({
    marginTop: "4px",
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.62rem",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.55)",
});

export const AvatarSlot = styled("div")({
    position: "absolute",
    transform: "translate(-50%, -50%)",
    width: "clamp(38px, 12%, 46px)",
});

export const Avatar = styled("img", {
    shouldForwardProp: (prop) => prop !== "state",
})<{ state: "current" | "received" | "awaiting" }>(({ state }) => ({
    display: "block",
    width: "100%",
    aspectRatio: "1 / 1",
    borderRadius: "50%",
    objectFit: "cover",
    transition: "all 0.4s cubic-bezier(0.22,0.61,0.36,1)",
    border:
        state === "current"
            ? `2px solid ${colors.secondaryMain}`
            : state === "received"
                ? `2px solid ${colors.mainSecondary}`
                : "2px solid rgba(255,255,255,0.22)",
    boxShadow:
        state === "current"
            ? "0 0 0 5px rgba(255,204,0,0.16), 0 10px 26px rgba(0,0,0,0.45)"
            : "0 6px 18px rgba(0,0,0,0.35)",
    filter: state === "awaiting" ? "grayscale(0.35) opacity(0.85)" : "none",
    transform: state === "current" ? "scale(1.14)" : "scale(1)",
}));

export const AvatarPulse = styled("span")({
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    border: `2px solid ${colors.secondaryMain}`,
    animation: `${pulseRing} 1.9s ease-out infinite`,
    pointerEvents: "none",
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
});

export const ContribChip = styled("span")({
    position: "absolute",
    left: "50%",
    bottom: "calc(100% + 6px)",
    transform: "translateX(-50%)",
    whiteSpace: "nowrap",
    borderRadius: "100px",
    padding: "3px 8px",
    background: "rgba(30,193,149,0.2)",
    border: "1px solid rgba(30,193,149,0.42)",
    color: "#7defc4",
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.6rem",
    fontWeight: 700,
    animation: `${riseIn} 0.4s ease both`,
});

export const LegendRow = styled("div")({
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "6px 14px",
    margin: "2px 0 14px",
});

export const LegendItem = styled("span")({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.68rem",
    color: "rgba(255,255,255,0.58)",
});

export const LegendDot = styled("span", {
    shouldForwardProp: (prop) => prop !== "tone",
})<{ tone: string }>(({ tone }) => ({
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: tone,
}));

export const MemberRow = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "9px 10px",
    borderRadius: "12px",
    background: "rgba(255,255,255,0.045)",
    border: "1px solid rgba(255,255,255,0.07)",
    transition: "background 0.3s ease, border-color 0.3s ease",
    "& + &": { marginTop: "7px" },
});

export const MemberName = styled("span")({
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.82rem",
    fontWeight: 600,
    color: "rgba(255,255,255,0.88)",
});

export const StatusChip = styled("span", {
    shouldForwardProp: (prop) => prop !== "tone",
})<{ tone: string }>(({ tone }) => ({
    flexShrink: 0,
    borderRadius: "100px",
    padding: "3px 10px",
    background: `${tone}22`,
    border: `1px solid ${tone}55`,
    color: tone,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.63rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
}));

export const FloatChip = styled("div")(({ theme }) => ({
    position: "absolute",
    zIndex: 3,
    display: "flex",
    alignItems: "center",
    gap: "9px",
    borderRadius: "14px",
    padding: "10px 14px",
    background: "#ffffff",
    border: "1px solid rgba(4,13,69,0.06)",
    boxShadow: "0 18px 40px rgba(1,6,32,0.4)",
    animation: `${floatY} 6s ease-in-out infinite`,
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    [theme.breakpoints.down("sm")]: { display: "none" },
}));

export const FloatChipLabel = styled("span")({
    display: "block",
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.62rem",
    color: "#7a8496",
    lineHeight: 1.3,
});

export const FloatChipValue = styled("span")({
    display: "block",
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.86rem",
    fontWeight: 800,
    color: colors.primary,
    lineHeight: 1.3,
});

// ── Sticky conversion bar ────────────────────────────────────────────────────

export const StickyBar = styled("div", {
    shouldForwardProp: (prop) => prop !== "shown",
})<{ shown: boolean }>(({ theme, shown }) => ({
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "18px",
    padding: "12px 20px calc(12px + env(safe-area-inset-bottom, 0px))",
    background: "rgba(4,13,69,0.94)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    borderTop: "1px solid rgba(255,255,255,0.1)",
    boxShadow: "0 -12px 40px rgba(1,6,32,0.4)",
    transform: shown ? "translateY(0)" : "translateY(110%)",
    opacity: shown ? 1 : 0,
    visibility: shown ? "visible" : "hidden",
    transition:
        "transform 0.4s cubic-bezier(0.22,0.61,0.36,1), opacity 0.3s ease, visibility 0.3s",
    [theme.breakpoints.down("sm")]: { gap: "10px", padding: "10px 14px calc(10px + env(safe-area-inset-bottom, 0px))" },
}));

export const StickyText = styled("span")(({ theme }) => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.95rem",
    fontWeight: 700,
    color: "#ffffff",
    [theme.breakpoints.down("md")]: { display: "none" },
}));

export const StickyPrimary = styled(Button)(({ theme }) => ({
    ...baseCta,
    padding: "11px 26px",
    fontSize: "0.92rem",
    background: "linear-gradient(135deg, #ffd02e 0%, #ffaa00 100%)",
    color: colors.primary,
    boxShadow: "0 8px 22px rgba(255,176,0,0.34)",
    "&:hover": {
        background: "linear-gradient(135deg, #ffe05a 0%, #ffbb1a 100%)",
        transform: "translateY(-1px)",
    },
    [theme.breakpoints.down("sm")]: { flex: 1, padding: "11px 14px" },
}));

export const StickyGhost = styled(Button)(({ theme }) => ({
    ...baseCta,
    padding: "11px 22px",
    fontSize: "0.92rem",
    color: "rgba(255,255,255,0.85)",
    border: "1px solid rgba(255,255,255,0.22)",
    "&:hover": {
        background: "rgba(255,255,255,0.08)",
        borderColor: "rgba(52,224,173,0.6)",
    },
    [theme.breakpoints.down("sm")]: { flex: 1, padding: "11px 14px" },
}));

export const StickyClose = styled("button")({
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 30,
    height: 30,
    borderRadius: "50%",
    border: "1px solid rgba(255,255,255,0.16)",
    background: "transparent",
    color: "rgba(255,255,255,0.6)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": { background: "rgba(255,255,255,0.1)", color: "#ffffff" },
});
